import React, { Component } from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { stripeObjectType } from 'types';

import * as yup from 'yup';
import has from 'lodash/has';

import { CardElement, injectStripe } from 'react-stripe-elements';
import { Field } from 'formik';

import Button, { GhostButton } from 'ui/Button';
import Form, { FormBody, FormFooter, FormSection } from 'ui/Form';
import Input from 'ui/Input';
import InputWrapper from 'components/InputWrapper';
import LoadingIndicator from 'ui/LoadingIndicator';
import Message from 'components/Message';
import StripeLink from 'components/StripeLink';
import Select from 'ui/Select';

import { states } from 'utils/countries';
import { colors } from 'css/constants';
import * as styles from './styles';

export class InnerForm extends Component {
  getElementError = (element) =>
    this.props.touched[element] && this.props.errors[element];

  onChangeCard = (change) => {
    const { setFieldError, setFieldValue } = this.props;

    if (!change.complete) {
      setFieldError('card', '(required)');
      setFieldValue('card', '');
    } else {
      setFieldError('card', undefined);
      setFieldValue('card', 'complete');
    }
  };

  // We need to reset state when country value changes
  onChangeCountry = (fieldName, selectedCountry) => {
    const { setFieldValue, values } = this.props;

    setFieldValue(fieldName, selectedCountry);

    if (values.country !== selectedCountry) {
      setFieldValue('state', '');
    }
  };

  handleSubmit = (event) => {
    const {
      errors,
      handleSubmit,
      onSubmit,
      resetForm,
      setErrors,
      setSubmitting,
      stripe,
      values
    } = this.props;

    const actions = {
      resetForm,
      setErrors,
      setSubmitting
    };

    // We need to remove the extra value we used for validation
    const { card, name, ...amendedValues } = values; // eslint-disable-line no-unused-vars

    // We usually handle logic like this in the container, but since we need to
    // have some of it here it is better to do it all in the same place
    const handleCardSetup = (clientSecret) =>
      stripe.handleCardSetup(clientSecret, {
        payment_method_data: {
          billing_details: {
            address: { ...amendedValues },
            name
          }
        }
      });

    // This is Formik's handler that we must call to validate the form
    handleSubmit(event);
    // This is our handler, that we use to pass non-Formik parameters to the root
    // (and, from there, to the saga).
    // We only call it if the form is complete (or if the only error is 'description')
    const errorsLength = Object.keys(errors).length;
    if (!errorsLength || (errorsLength === 1 && errors.description)) {
      onSubmit(handleCardSetup, actions);
    }
  };

  renderErrorMessage = () =>
    this.props.errors.description && (
      <Message type={Message.types.ERROR} withMarginBottom>
        {this.props.errors.description}
      </Message>
    );

  renderStateInput = (country = '') => {
    const { setFieldTouched, setFieldValue, touched, values } = this.props;
    const { state } = values;
    const countryCode = country.code;
    const STATE = 'state';
    const stateError = this.getElementError(STATE);

    const stateConfig = {
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ code }) => code,
      menuPlacement: 'top'
    };

    // Some countries have state data available; if we have it, show a <Select />,
    // otherwise show an <Input />
    let stateSelectOpts;
    let stateSelectValue;

    if (countryCode in states) {
      stateSelectOpts = states[countryCode];
      stateSelectValue = states[countryCode].find(({ code }) => code === state);
    }

    const stateListAvailable = !!stateSelectOpts;

    if (stateListAvailable) {
      return (
        <Select
          config={stateConfig}
          haserror={!!stateError}
          id={STATE}
          key={`${countryCode}StateSelect`}
          name={STATE}
          onBlur={setFieldTouched}
          onChange={setFieldValue}
          options={stateSelectOpts}
          touched={touched[STATE]}
          value={stateSelectValue}
        />
      );
    }

    return (
      <Input
        as={Field}
        data-testid="genericStateSelect"
        haserror={stateError}
        id={STATE}
        name={STATE}
      />
    );
  };

  renderFormElements = () => {
    const { countries, setFieldTouched, touched, values } = this.props;

    const CARD = 'card';
    const cardError = this.getElementError(CARD);
    const cardLabel = 'Card details';

    // Custom styling for the Stripe `CardElement` (https://github.com/stripe/elements-examples)
    const cardStyles = {
      base: {
        fontFamily: "Lato, 'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: '16px',
        '::placeholder': {
          color: `${colors.placeholder}`
        }
      },
      invalid: {
        color: `${colors.red}`
      }
    };
    const oncardBlur = () => setFieldTouched(CARD);
    const oncardReady = (element) => element.focus();

    const NAME = 'name';
    const nameError = this.getElementError(NAME);
    const nameLabel = 'Name on card';

    const LINE_1 = 'line1';
    const line1Error = this.getElementError(LINE_1);
    const line1Label = 'Line 1';

    const LINE_2 = 'line2';
    const line2Error = this.getElementError(LINE_2);
    const line2Label = 'Line 2';

    const CITY = 'city';
    const cityError = this.getElementError(CITY);
    const cityLabel = 'City';

    const STATE = 'state';
    const stateError = this.getElementError(STATE);
    const stateLabel = 'State';

    const POSTAL_CODE = 'postal_code';
    const postalCodeError = this.getElementError(POSTAL_CODE);
    const postalCodeLabel = 'Zip/Postal Code';

    const COUNTRY = 'country';
    const countryError = this.getElementError(COUNTRY);
    const countryLabel = 'Country';

    const countrySelectValue = countries.find(
      ({ code }) => code === values.country
    );

    const countryConfig = {
      getOptionLabel: (option) => option.name,
      getOptionValue: (option) => option.code
    };

    return (
      <FormSection>
        <InputWrapper
          fullWidth
          label={cardLabel}
          name={CARD}
          error={cardError}
          withMarginBottom
        >
          <Input
            as={CardElement}
            haserror={cardError}
            hidePostalCode
            onBlur={oncardBlur}
            onChange={this.onChangeCard}
            onReady={oncardReady}
            style={cardStyles}
            suppressClassNameWarning
          />
        </InputWrapper>
        <InputWrapper error={nameError} fullWidth label={nameLabel} name={NAME}>
          <Input
            as={Field}
            data-testid={NAME}
            haserror={nameError}
            id={NAME}
            name={NAME}
          />
        </InputWrapper>
        <styles.SectionTitle>Billing Information</styles.SectionTitle>
        <InputWrapper
          fullWidth
          label={countryLabel}
          name={COUNTRY}
          error={countryError}
          withMarginBottom
        >
          <Select
            config={countryConfig}
            data-testid={COUNTRY}
            haserror={!!countryError}
            id={COUNTRY}
            name={COUNTRY}
            onBlur={setFieldTouched}
            onChange={this.onChangeCountry}
            options={countries}
            touched={touched[COUNTRY]}
            value={countrySelectValue}
          />
        </InputWrapper>
        <InputWrapper
          error={line1Error}
          label={line1Label}
          name={LINE_1}
          withMarginBottom
        >
          <Input
            as={Field}
            data-testid={LINE_1}
            haserror={line1Error}
            id={LINE_1}
            name={LINE_1}
          />
        </InputWrapper>
        <InputWrapper
          error={line2Error}
          label={line2Label}
          name={LINE_2}
          withMarginBottom
        >
          <Input
            as={Field}
            data-testid={LINE_2}
            haserror={line2Error}
            id={LINE_2}
            name={LINE_2}
          />
        </InputWrapper>
        <InputWrapper
          error={cityError}
          label={cityLabel}
          name={CITY}
          withMarginBottom
        >
          <Input
            as={Field}
            data-testid={CITY}
            haserror={cityError}
            id={CITY}
            name={CITY}
          />
        </InputWrapper>
        <InputWrapper label={stateLabel} name={STATE} error={stateError}>
          {this.renderStateInput(countrySelectValue)}
        </InputWrapper>
        <InputWrapper
          error={postalCodeError}
          label={postalCodeLabel}
          name={POSTAL_CODE}
          withMarginBottom
        >
          <Input
            as={Field}
            data-testid={POSTAL_CODE}
            haserror={postalCodeError}
            id={POSTAL_CODE}
            name={POSTAL_CODE}
          />
        </InputWrapper>
      </FormSection>
    );
  };

  renderButtons = () => (
    <FormSection noMarginBottom>
      <GhostButton
        disabled={this.props.isSubmitting}
        name="cancelBtn"
        onClick={this.props.hideModal}
        type="button"
      >
        Cancel
      </GhostButton>
      <Button disabled={this.props.isSubmitting} name="submitBtn" type="submit">
        Confirm
      </Button>
    </FormSection>
  );

  render() {
    const { isSubmitting } = this.props;

    return (
      <Form onSubmit={this.handleSubmit}>
        {isSubmitting && <LoadingIndicator cover />}
        <FormBody>
          {this.renderErrorMessage()}
          {this.renderFormElements()}
          <StripeLink />
        </FormBody>
        <FormFooter>{this.renderButtons()}</FormFooter>
      </Form>
    );
  }
}

// NOTE: this needs to be exported openly because the default export is the HOC
// and not the form component, so setting it as its property doesn't work
export const validationSchema = yup.object({
  city: yup.string().required('(required)'),
  country: yup.string().required('(required)'),
  line1: yup.string().required('(required)'),
  line2: yup.string(),
  state: yup.string().when('country', {
    is: (val) => has(states, val),
    then: yup.string().required('(required)')
  }),
  postal_code: yup.string().required('(required)'),
  card: yup.string().required('(required)'),
  name: yup.string().required('(required)')
});

InnerForm.propTypes = {
  countries: arrayOf(
    shape({
      code: string.isRequired,
      name: string.isRequired
    })
  ).isRequired,
  errors: object,
  handleSubmit: func,
  isSubmitting: bool,
  hideModal: func.isRequired,
  onSubmit: func,
  resetForm: func,
  setErrors: func,
  setFieldError: func,
  setFieldTouched: func,
  setFieldValue: func,
  setSubmitting: func,
  stripe: stripeObjectType.isRequired,
  touched: object,
  values: object
};

export default injectStripe(InnerForm);
